/**
 * SpineControlBar — control strip for pause/resume/stop during active runs.
 *
 * Renders below the spine tab when a run is in progress (mid-run, pausing, paused).
 * Shows pause/resume toggle and stop button, mirroring the TabBar's layout.
 *
 * SRP: pure presentation. Receives run state + callbacks; emits control intents.
 */

import type React from "react";
import { Button } from "../../ds/components";

interface SpineControlBarProps {
  /** Current run state — determines which controls render and their labels. */
  runState: "mid-run" | "pausing" | "paused";
  /** Handler for pause intent (mid-run/approval). */
  onPause?: () => void;
  /** Handler for resume intent (paused). */
  onResume?: () => void;
  /** Handler for opening kill confirmation dialog. */
  onShowKillConfirmation?: () => void;
  /** "Draft now": in-flight sections draft from what they have gathered; the
   *  run carries on with the rest as normal. Rendered only mid-run. */
  onDraftNow?: () => void;
  /** True once Draft now was sent and the in-flight sections are finishing. */
  draftNowPending?: boolean;
  /** When true, disable all controls (e.g., during a pause/resume in-flight). */
  disabled?: boolean;
  /** Server-given reason for the pause (e.g. provider out of credits). */
  notice?: string | null;
}

export const SpineControlBar: React.FC<SpineControlBarProps> = ({
  runState,
  onPause,
  onResume,
  onShowKillConfirmation,
  onDraftNow,
  draftNowPending = false,
  disabled = false,
  notice = null,
}) => {
  const isPaused = runState === "paused";
  const isPausing = runState === "pausing";

  // Design board 09: Pause · Stop run under the progress bar; Draft now on the right.
  return (
    <div className="as-stack-8">
      {isPaused && notice ? (
        <div className="as-notice as-notice-warning" role="status">
          {notice}
        </div>
      ) : draftNowPending && !isPaused ? (
        <output className="as-note">Drafting the sections in progress from what was gathered…</output>
      ) : null}
      <div className="as-spinehead-actions" role="toolbar" aria-label="Run controls">
        {!isPaused && (
          <Button
            variant="secondary"
            size="sm"
            onClick={onPause}
            disabled={disabled || isPausing}
            aria-label={`Pause run${isPausing ? " (in progress)" : ""}`}
          >
            {isPausing ? "Pausing…" : "Pause"}
          </Button>
        )}
        {isPaused && (
          <Button variant="primary" size="sm" onClick={onResume} disabled={disabled} aria-label="Resume run">
            Resume
          </Button>
        )}
        {onShowKillConfirmation && (
          <Button variant="danger" size="sm" onClick={onShowKillConfirmation} disabled={disabled} aria-label="Stop the run">
            Stop run
          </Button>
        )}
        <span className="as-grow" />
        {onDraftNow && !isPaused && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onDraftNow}
            disabled={disabled || isPausing || draftNowPending}
            title="Stop retrieving and draft the sections in progress from what has been gathered; the rest of the run continues as normal"
            aria-label="Draft the sections in progress now"
          >
            Draft now
          </Button>
        )}
      </div>
    </div>
  );
};

export default SpineControlBar;
